import { createServerFn } from "@tanstack/react-start";
import { commitMutation, Environment, graphql } from "relay-runtime";
import { z } from "zod";
import type { createAccountMutation } from "./__generated__/createAccountMutation.graphql";
import { useSession } from "@tanstack/react-start/server";
import { relayNetwork } from "@/relay/network";
import { type SessionData, sessionOptions } from "@/utils/getSession";
import { getClub, getCurrentHostUrl } from "@/utils/getClub";
import * as Sentry from "@sentry/node";

export const createAccount = createServerFn({ method: "POST" })
	.inputValidator(
		z.object({
			code: z.string().optional(),
			username: z
				.string()
				.min(2)
				.regex(/^[a-zA-Z0-9_-]+$/),
			email: z.string().email(),
			password: z.string().min(1),
		}),
	)
	.handler(async ({ data }): Promise<Partial<SessionData>> => {
		try {
			const host = await getCurrentHostUrl();
			const club = await getClub({ data: { host: host! } });
			// biome-ignore lint/correctness/useHookAtTopLevel: this is not a hook
			const session = await useSession(sessionOptions);

			return new Promise((resolve, reject) => {
				commitMutation<createAccountMutation>(
					new Environment({
						network: relayNetwork,
					}),
					{
						mutation: graphql`
  			    mutation createAccountMutation($payload: CreateAccountInput!) {
  							createAccount(payload: $payload) {
     							accessToken
     							refreshToken
     							expiresIn
     							user {
     					      id
                    email
                    username
                    displayName
                    avatarFallback
                    photo {
                        signedUrl
                    }
                    clubs {
                      id
                    }
                    rawID
  							}
  						}
  					}
  				`,
						variables: {
							payload: {
								username: data.username,
								club: club?.slug,
								invite: data.code,
								email: data.email,
								password: data.password,
							},
						},
						onCompleted: (response) => {
							session
								.update({
									...session?.data,
									isLoggedIn: true,
									accessToken: response.createAccount.accessToken,
									refreshToken: response.createAccount.refreshToken,
									user: response.createAccount.user,
									expiresAt: new Date(
										Date.now() +
											response.createAccount.expiresIn -
											15 * 60 * 1000,
									).toISOString(),
								})
								.then(() => resolve(session?.data));
						},
						onError: (
							err: Error & { source?: { errors?: { message: string }[] } },
						) => {
							Sentry.captureException(err);

							if ("source" in err && err.source?.errors?.[0]) {
								reject(new Error(err.source?.errors?.[0].message));
							} else {
								reject(err);
							}
						},
					},
				);
			});
		} catch (err) {
			Sentry.captureException(err);

			throw err;
		}
	});
